import { useRef, useEffect } from 'react';

/**
 * Custom hook which will fire the supplied onClickOutside & onKeyupOutside handlers when a user
 * clicks outside of the element tagged with the returned ref, and the isExpanded state is true.
 *
 * @param isExpanded The expanded state of the control.
 * @param onClickOutside An event handler which is run when a user clicks outside of the control.
 * @param onKeyupOutside An event handler which is run when a user presses a key outside of the control.
 * @returns {*[]} A ref which can should be used to set the element outside of which clicking will cause the event handlers to execute.
 */
export const useClickOutside = (isExpanded, onClickOutside, onKeyupOutside) => {
  const node = useRef();

  const handleClickOutside = (event) => {
    // istanbul ignore else
    if (node.current && !node.current.contains(event.target)) {
      onClickOutside(event);
    }
  };

  const handleKeyupOutside = (event) => {
    // istanbul ignore else
    if (node.current && !node.current.contains(event.target)) {
      onKeyupOutside(event);
    }
  };

  const manageOutsideListeners = (isAdd) => {
    const outsideListeners = [
      { type: 'click', fn: handleClickOutside },
      { type: 'keyup', fn: handleKeyupOutside },
    ];
    outsideListeners.forEach((listenerInfo) => {
      if (isAdd) {
        document.addEventListener(listenerInfo.type, listenerInfo.fn);
      } else {
        document.removeEventListener(listenerInfo.type, listenerInfo.fn);
      }
    });
  };

  // Handle keyup and click events which occur outside of this ref
  useEffect(() => {
    if (isExpanded) {
      manageOutsideListeners(true);
    } else {
      manageOutsideListeners(false);
    }
    return () => {
      manageOutsideListeners(false);
    };
  }, [isExpanded]);

  return [node];
};
